import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ClientDashboard from "./pages/ClientDashboard";
import ProviderDashboard from "./pages/ProviderDashboard";
import DiscoveryPage from "./pages/DiscoveryPage";
import DiagnosticPage from "./pages/DiagnosticPage";
import ProvidersPage from "./pages/ProvidersPage";
import BudgetsPage from "./pages/BudgetsPage";
import NegotiationPage from "./pages/NegotiationPage";
import ContractsPage from "./pages/ContractsPage";
import ProfilePage from "./pages/ProfilePage";
import ExecutionPage from "./pages/ExecutionPage";
import PaymentPage from "./pages/PaymentPage";
import DirectProviderPage from "./pages/DirectProviderPage";
import DirectServicePage from "./pages/DirectServicePage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import TipPage from "./pages/TipPage";
import NewSubscriptionPage from "./pages/NewSubscriptionPage";
import BottomNavigation from "./components/BottomNavigation";

// Project/Construction Pages (Minha Obra)
import ProjectPage from "./pages/ProjectPage";
import ProjectsListPage from "./pages/ProjectsListPage";
import ProjectCreatePage from "./pages/ProjectCreatePage";
import ProjectTimelinePage from "./pages/ProjectTimelinePage";
import ProjectDiaryPage from "./pages/ProjectDiaryPage";
import ProjectMaterialsPage from "./pages/ProjectMaterialsPage";
import ProjectMeasurementPage from "./pages/ProjectMeasurementPage";
import ProjectAddendumPage from "./pages/ProjectAddendumPage";

// Admin
import AdminPage from "./pages/AdminPage";

// IA & Contracts
import AssistantPage from "./pages/AssistantPage";
import ContractSignaturePage from "./pages/ContractSignaturePage";
import FloatingAssistant from "./components/FloatingAssistant";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("fullhome_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("fullhome_token");
      if (storedToken) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
          } else {
            localStorage.removeItem("fullhome_token");
            setToken(null);
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("fullhome_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem("fullhome_token", accessToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("fullhome_token");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/session`, {
            headers: { "X-Session-ID": sessionId }
          });
          
          if (response.ok) {
            const data = await response.json();
            login({
              user_id: data.user_id,
              email: data.email,
              name: data.name,
              role: data.role,
              picture: data.picture
            }, data.access_token);
            
            navigate(data.role === "provider" ? "/provider" : "/client", { replace: true });
          } else {
            navigate("/login", { replace: true });
          }
        } catch (error) {
          console.error("OAuth callback error:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };
    
    processAuth();
  }, [location, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Autenticando...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "provider" ? "/provider" : "/client"} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Client Routes */}
      <Route path="/client" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/discovery" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <DiscoveryPage />
        </ProtectedRoute>
      } />
      <Route path="/diagnostic/:categoryId" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <DiagnosticPage />
        </ProtectedRoute>
      } />
      <Route path="/providers/:categoryId" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <ProvidersPage />
        </ProtectedRoute>
      } />
      
      {/* Provider Routes */}
      <Route path="/provider" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderDashboard />
        </ProtectedRoute>
      } />
      
      {/* Shared Routes */}
      <Route path="/budgets" element={
        <ProtectedRoute>
          <BudgetsPage />
        </ProtectedRoute>
      } />
      <Route path="/negotiations/:negotiationId" element={
        <ProtectedRoute>
          <NegotiationPage />
        </ProtectedRoute>
      } />
      <Route path="/contracts" element={
        <ProtectedRoute>
          <ContractsPage />
        </ProtectedRoute>
      } />
      <Route path="/execution/:contractId" element={
        <ProtectedRoute>
          <ExecutionPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/:escrowId" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <PaymentPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Direct Provider Flow (Já tenho um profissional) */}
      <Route path="/direct-provider" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <DirectProviderPage />
        </ProtectedRoute>
      } />
      <Route path="/direct-service/:providerId/:categoryId" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <DirectServicePage />
        </ProtectedRoute>
      } />
      
      {/* Subscriptions */}
      <Route path="/subscriptions" element={
        <ProtectedRoute>
          <SubscriptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/subscriptions/new" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <NewSubscriptionPage />
        </ProtectedRoute>
      } />
      
      {/* Tips */}
      <Route path="/tip/:contractId" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <TipPage />
        </ProtectedRoute>
      } />
      
      {/* Projects / Minha Obra (Construção de Casa) */}
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsListPage />
        </ProtectedRoute>
      } />
      <Route path="/projects/new" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <ProjectCreatePage />
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId" element={
        <ProtectedRoute>
          <ProjectPage />
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/timeline" element={
        <ProtectedRoute>
          <ProjectTimelinePage />
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/diary" element={
        <ProtectedRoute>
          <ProjectDiaryPage />
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/materials" element={
        <ProtectedRoute>
          <ProjectMaterialsPage />
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/measurement/:stageId" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <ProjectMeasurementPage />
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/addendums" element={
        <ProtectedRoute>
          <ProjectAddendumPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Panel */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminPage />
        </ProtectedRoute>
      } />
      
      {/* Assistente IA eZelo. */}
      <Route path="/assistant" element={
        <ProtectedRoute>
          <AssistantPage />
        </ProtectedRoute>
      } />
      
      {/* Assinatura Digital */}
      <Route path="/contracts/:contractId/sign" element={
        <ProtectedRoute>
          <ContractSignaturePage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <BottomNavigation />
        <FloatingAssistant />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
